import { Alert, Autocomplete, Box, Checkbox, FormControl, FormControlLabel, FormLabel, Grid, LinearProgress, Link, List, ListItem, ListItemText, Paper, Radio, RadioGroup, Slider, TextField, ToggleButton, Typography } from "@mui/material";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useEffect, useRef, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useNavigate, useParams } from "react-router-dom";
import SignatureCanvas from 'react-signature-canvas';
import { BrandEnum } from "../../_common/enum/brand.enum";
import { FormFieldKeyActionEnum } from "../../_common/enum/forms/formFieldKeyAction.enum";
import { FormFieldKeyConditionEnum } from "../../_common/enum/forms/formFieldKeyCondition.enum";
import { FormFieldKeyConditionTypeEnum } from "../../_common/enum/forms/formFieldKeyConditionType.enum";
import { FormFieldKeySpecialTypeEnum } from "../../_common/enum/forms/formFieldKeySpecialType.enum";
import { FormFieldValueTypeEnum } from "../../_common/enum/forms/formFieldValueType.enum";
import { FormTemplateTypeEnum } from "../../_common/enum/forms/formTemplateType.enum";
import { MortgageTypeEnum } from "../../_common/enum/mortgageType.enum";
import { ScreenSizeCategoryEnum } from "../../_common/enum/screenSizeCategory.enum";
import { IFormFieldManage } from "../../_common/models/formField";
import { IFormFieldKeyManage } from "../../_common/models/formFieldKey";
import { IFormTemplateSectionFormManage } from "../../_common/models/formTemplateSection/formTemplateSectionFormManage.model";
import CenteredAlert from "../../_components/alert/centeredAlert";
import { DataTypeAutoComplete } from "../../_components/autocomplete/dataType.autoComplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { PRIMARY_COLOR } from "../../_const/color.const";
import { getMoneyFormatInteger, getMoneyFormatNoFractions } from "../../_helpers/format.helper";
import { applyValidationRules, removeValidationRules } from "../../_helpers/formValidation.helper";
import { handleErrorResponseAxios } from "../../_helpers/handleResponse.helper";
import { retryHeight, sendContainerHeightToParent } from "../../_helpers/iFrameSize.helper";
import { handleInput } from "../../_helpers/input.helper";
import { notifyParentConversionWithAudit } from "../../_helpers/quoteConversion.helper";
import { getWindowSizeCategory, useScreenSize } from "../../_helpers/screenSize.helper";
import { formSectionService } from "../../_services/formSection.service";
import { formTemplateSectionService } from "../../_services/formTemplateSection.service";


export function FormSectionManage(params: {
    formTemplateSectionId: number,
    formSectionId?: number,
    isSectionSaving?: boolean,
    isForReview?: boolean,
    onSave: () => void,
    actionId?: FormFieldKeyActionEnum,
    isFormCompleted?: boolean,
    activeStep?: number | null,
    formTemplateName?: string,
    formTemplateTypeId?: FormTemplateTypeEnum,
    brand: BrandEnum
}) {

    interface DateFields {
        dobDay: string,
        dobMonth: string,
        dobYear: string,
        formattedDobDate: string,
        jlDobDay: string,
        jlDobMonth: string,
        jlDobYear: string,
        formattedJlDobDate: string,
    }

    const signatureRef = useRef(null);

    const [data, setData] = useState<IFormTemplateSectionFormManage>();
    const [screenSizeCategory, setScreenSizeCategory] = useState<ScreenSizeCategoryEnum | null>();
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [alertOpen, setAlertOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const { formId } = useParams<{ formId: string }>();
    const _formId = Number(formId ?? 0);
    const navigate = useNavigate();
    const screenSize = useScreenSize();
    //const parentOrigin = useRef<string>(normalizeOrigin(config.ORIGIN || ""));


    const [dateFields, setDateFields] = useState<DateFields>({
        dobDay: "",
        dobMonth: "",
        dobYear: "",
        formattedDobDate: "",
        jlDobDay: "",
        jlDobMonth: "",
        jlDobYear: "",
        formattedJlDobDate: "",
    });

    const dobMonthRef = useRef<HTMLInputElement>(null);
    const dobYearRef = useRef<HTMLInputElement>(null);
    const jlDobMonthRef = useRef<HTMLInputElement>(null);
    const jlDobYearRef = useRef<HTMLInputElement>(null);

    dayjs.extend(customParseFormat);


    const userStorageData = () => ({
        FirstName: '',
        LastName: '',
        DateOfBirth: '',
        EmailAddress: '',
        PhoneNumber: '',
        SmokingStatus: '',
        Gender: '',
        NumberOfCovers: '',
        FirstNameJointLife: null,
        LastNameJointLife: null,
        DateOfBirthJointLife: null,
    });


    const [userData, setUserData] = useState(() => {
        // Load initial data from localStorage
        const savedData = localStorage.getItem('userData');
        return savedData ? JSON.parse(savedData) : { userStorageData };
    });

    useEffect(() => {
        if (dateFields && (dateFields.jlDobDay == "00" || dateFields.jlDobMonth == "00")) {
            setDateFields({ ...dateFields, jlDobDay: "", jlDobMonth: "" })
        }
    }, [dateFields])

    useEffect(() => {
        if (!screenSize) return;
        setScreenSizeCategory(getWindowSizeCategory(screenSize.width));
    }, [screenSize]);

    useEffect(() => {
        fetchTemplateSectionData();
    }, [params.formTemplateSectionId]);


    useEffect(() => {
        applyValidationRules();
        return () => {
            removeValidationRules();
        };
    }, []);


    const getEnumValue = (enumObj: any, key: string): number => {
        return enumObj[key];
    };
    function fetchTemplateSectionData() {
        if (params.formTemplateSectionId == 0) return;

        setLoading(true);
        return formTemplateSectionService
            .getForm(params.formTemplateSectionId, _formId, params.isForReview, params.actionId)
            .then(
                (json) => {
                    setData(json);

                    const savedData = localStorage.getItem('userData');

                    if (!!savedData && savedData != "undefined") {
                        Object.entries(JSON.parse(savedData)).filter(([key, value]) => key && value).map(([key, value]) => {

                            var enumValue = getEnumValue(FormFieldKeySpecialTypeEnum, key);
                            var item = json.formFieldKeys.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === enumValue)[0];

                            // when switching from mortgages to other forms it saves "Single" or "Joint"
                            if (!!item && item.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.NumberOfCovers && params.formTemplateName != "Mortgages") {
                                const mapFieldValue = { Single: "One", Joint: "Two" } as const;
                                if (value as string in mapFieldValue) {
                                    value = mapFieldValue[value as keyof typeof mapFieldValue];
                                }
                            }

                            if (!!item) {
                                item.formField = {
                                    id: 0,
                                    fieldValue: value,
                                    formFieldKeyId: item.id,
                                    formTemplateSectionId: item.formTemplateSectionId,
                                    formSectionId: params.formSectionId ?? 0
                                }
                            }

                        });
                    }

                    // if new form populate with local storage items

                    if (!!json.formFieldKeys && json.formFieldKeys.every((ffk: IFormFieldKeyManage) => ffk.formField == null) && !!userData) {
                        Object.entries(userData).filter(([key, value]) => key && value).map(([key, value]) => {

                            var enumValue = getEnumValue(FormFieldKeySpecialTypeEnum, key);
                            var item = json.formFieldKeys.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === enumValue)[0];

                            if (!!item) {
                                item.formField = {
                                    id: 0,
                                    fieldValue: value,
                                    formFieldKeyId: item.id,
                                    formTemplateSectionId: item.formTemplateSectionId,
                                    formSectionId: params.formSectionId ?? 0
                                }
                            }

                        });
                    }

                    if (params.activeStep === 0 && !!json.formFieldKeys &&
                        json.formFieldKeys.filter((ffk: IFormFieldKeyManage) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth
                            || ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirthJointLife)) {

                        var dobField = json.formFieldKeys.filter((ffk: IFormFieldKeyManage) => ffk.shortName === 'dateofbirth')[0];
                        var jlDobField = json.formFieldKeys.filter((ffk: IFormFieldKeyManage) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirthJointLife);

                        setDateFields({
                            ...dateFields, dobDay: !!dobField ? dobField.formField && dobField.formField.fieldValue ? dobField.formField.fieldValue.split("/")[0] : "" : "",
                            dobMonth: !!dobField ? dobField.formField && dobField.formField.fieldValue ? dobField.formField.fieldValue.split("/")[1] : "" : "",
                            dobYear: !!dobField ? dobField.formField && dobField.formField.fieldValue ? dobField.formField.fieldValue.split("/")[2] : "" : "",
                            jlDobDay: jlDobField && jlDobField.length && jlDobField[0].formField && jlDobField[0].formField.fieldValue ? jlDobField[0].formField.fieldValue.split("/")[0] : "",
                            jlDobMonth: jlDobField && jlDobField.length && jlDobField[0].formField && jlDobField[0].formField.fieldValue ? jlDobField[0].formField.fieldValue.split("/")[1] : "",
                            jlDobYear: jlDobField && jlDobField.length && jlDobField[0].formField && jlDobField[0].formField.fieldValue ? jlDobField[0].formField.fieldValue.split("/")[2] : "",

                        })
                    }

                    // Set default BER to "I don't know"
                    if (params.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && params.activeStep === 1
                        && !!json.formFieldKeys && json.formFieldKeys.filter((ffk: IFormFieldKeyManage) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.BerRating)) {

                        var item = json.formFieldKeys.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.BerRating)[0];

                        if (!!item) {
                            item.formField = {
                                id: 0,
                                fieldValue: "I don't know",
                                formFieldKeyId: item.id,
                                formTemplateSectionId: item.formTemplateSectionId,
                                formSectionId: params.formSectionId ?? 0
                            }
                        }
                    }

                    return json.formFieldKeys;
                }
            )
            .finally(() => {
                setLoading(false);
                retryHeight(sendContainerHeightToParent);
            });
    }

    function handleAutocompleteChange(event: any, ffkId: any, value: any) {
        event.preventDefault();
        setData((_data: any) => {
            var __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                var _formFieldKey = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.id === ffkId);

                if (!!_formFieldKey.formField) {
                    _formFieldKey.formField.fieldValue = value
                }

                else {
                    _formFieldKey.formField = {
                        id: 0,
                        fieldValue: value,
                        formFieldKeyId: _formFieldKey.id,
                        formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                        formSectionId: params.formSectionId ?? 0
                    };
                }
            }

            return { ..._data } as IFormTemplateSectionFormManage;
        });

        // bumpIframeHeight();
    }

    function handleChangeDataType(event: any, ffkId: number) {
        setData((_data: any) => {
            var __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                var _formFieldKey = __data.formFieldKeys.find((ffk: IFormFieldKeyManage) => ffk.id === ffkId);

                if (_formFieldKey.formField) {
                    _formFieldKey.formField.fieldValue = event.target.value;
                }
                else {
                    _formFieldKey.formField = {
                        id: 0,
                        fieldValue: event.target.value,
                        formFieldKeyId: _formFieldKey.id,
                        formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                        formSectionId: params.formSectionId ?? 0
                    };
                }
            }
            return { ..._data } as IFormTemplateSectionFormManage;
        });

        //  bumpIframeHeight();
    };

    function handleChange(event: any, ffkId: number, isCheckbox?: boolean, isPhone?: boolean) {
        if (event?.preventDefault) event.preventDefault();

        let value = isCheckbox === true
            ? event.target.checked ? "Yes" : "No"
            : event.target.value;

        if (isPhone === true) {
            const phoneRegex = /^[0-9+\-()\s]*$/;
            if (!phoneRegex.test(value)) {
                return;
            }

            const digitCount = value.replace(/\D/g, "").length;

            if (digitCount > 15) {
                return;
            }
        }

        setData((_data: any) => {
            const __data = { ..._data };

            if (__data.formFieldKeys && __data.formFieldKeys.length) {
                const _formFieldKey = __data.formFieldKeys.find(
                    (ffk: IFormFieldKeyManage) => ffk.id === ffkId
                );

                if (_formFieldKey?.formField) {
                    _formFieldKey.formField.fieldValue = value;
                } else if (_formFieldKey) {
                    _formFieldKey.formField = {
                        id: 0,
                        fieldValue: value,
                        formFieldKeyId: _formFieldKey.id,
                        formTemplateSectionId: _formFieldKey.formTemplateSectionId,
                        formSectionId: params.formSectionId ?? 0
                    };
                }
            }

            return { ...__data } as IFormTemplateSectionFormManage;
        });
    }

    function getQuotes() {
        if (params.formTemplateTypeId === FormTemplateTypeEnum.Mortgages) {
            navigate("/Mortgages/" + formId);
        } else {
            navigate("/Quotes/" + params.formTemplateName + "/" + formId);
        }
    }

    function getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(formFieldKeySpecialTypeEnum: FormFieldKeySpecialTypeEnum) {
        return data?.formFieldKeys?.some(ffk => ffk.formFieldKeySpecialTypeId === formFieldKeySpecialTypeEnum) ? data.formFieldKeys?.find(ffk => ffk.formFieldKeySpecialTypeId === formFieldKeySpecialTypeEnum)?.formField?.fieldValue : userData[FormFieldKeySpecialTypeEnum[formFieldKeySpecialTypeEnum]];
    }

    function buildDateFromParts(day?: string | number, month?: string | number, year?: string | number) {
        const d = (day ?? '').toString().trim();
        const m = (month ?? '').toString().trim();
        const y = (year ?? '').toString().trim();
        if (!d || !m || !y) return '';
        return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
    function getDobValue(
        type: FormFieldKeySpecialTypeEnum,
        d?: string | number,
        m?: string | number,
        y?: string | number
    ) {
        const key = FormFieldKeySpecialTypeEnum[type];
        const raw = data?.formFieldKeys?.find(ffk => ffk.formFieldKeySpecialTypeId === type)?.formField?.fieldValue;

        if (raw && raw.trim() !== '') return raw;             // use existing field value if not empty
        const composed = buildDateFromParts(d, m, y);
        if (composed) return composed;                         // otherwise build from split inputs
        return userData?.[key] ?? '';                          // finally fall back to prior saved value
    }

    const conversionSentRef = useRef(false);

    const notifyParentConversion = () => {
        if (conversionSentRef.current) return;
        conversionSentRef.current = true;

        if (window.parent) {
            window.parent.postMessage(
                {
                    type: "conversion",
                    event: "quote_form_submission", // you�ll trigger on this in GTM
                    transaction_id: formId, // use as transaction_id for dedupe
                },
                "*" //let parent validate origin
                //  parentOrigin.current
            );
        }
    };
    function submit(event: any) {
        event.preventDefault();

        //if (params.activeStep == 1) {
        //    notifyParentConversion();
        //}


        if (params.activeStep !== 0 && data && data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.TermsAndConditions)) {
            const fieldValue = data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.TermsAndConditions).find((x: any) => x.formField?.fieldValue)
            if (fieldValue?.formField?.fieldValue !== "Yes") {
                setAlertOpen(true);
                return;
            }
        }
        // Logic to limit mortgage cover amount
        if (data && data.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && params.activeStep === 1) {

            const incomeFieldValue = Number(data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0]?.formField?.fieldValue);
            const coverAmountFieldValue = Number(data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount)[0]?.formField?.fieldValue);
            const mortgageTypeFieldValue = data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.MortgageType)[0]?.formField?.fieldValue;
            const propertyValueFieldValue = Number(data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.PropertyValue)[0]?.formField?.fieldValue);

            if (incomeFieldValue && coverAmountFieldValue && incomeFieldValue * 4 < coverAmountFieldValue) {
                toastr.error(`You can borrow up to 4x your income. Maximum allowed: ${getMoneyFormatNoFractions(incomeFieldValue * 4)}`);
                return;
            }
            if (propertyValueFieldValue && coverAmountFieldValue && mortgageTypeFieldValue !== MortgageTypeEnum.buytolet && coverAmountFieldValue > propertyValueFieldValue * 0.9) {
                toastr.error(`You can borrow up to 90% the property value. Maximum allowed: ${getMoneyFormatNoFractions(propertyValueFieldValue * 0.9)}`);
                return;
            }
            if (propertyValueFieldValue && coverAmountFieldValue && mortgageTypeFieldValue === MortgageTypeEnum.buytolet && coverAmountFieldValue > propertyValueFieldValue * 0.7) {
                toastr.error(`You can borrow up to 70% the property value. Maximum allowed: ${getMoneyFormatNoFractions(propertyValueFieldValue * 0.7)}`);
                return;
            }
        }

        if (data && data.formTemplateTypeId === FormTemplateTypeEnum.IncomeProtection && params.activeStep === 1) {

            const occupationFieldValue = data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Occupation)[0]?.formField?.fieldValue;
            const occupationalClassFieldValue = data.formFieldKeys?.filter((ffk) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.OccupationalClass)[0]?.formField?.fieldValue;

            if (!occupationFieldValue && !occupationalClassFieldValue) {
                toastr.error("Please select your occupation or occupational class");
                return;
            }
        }

        var _formFields: Array<any> = [];


        if (data && data.formFieldKeys && data.formFieldKeys.length) {

            _formFields = data.formFieldKeys.map(ffk => {
                var _formField: IFormFieldManage | { id: number, fieldValue: string, fieldValueTest: string, formFieldKeyId: number, formTemplateSectionId: number, formSectionId: number }

                if (!!ffk.formField && ffk.formField.fieldValue && ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth) {
                    ffk.formField.fieldValue = `${dateFields.dobDay.padStart(2, "0")}/${dateFields.dobMonth.padStart(2, "0")}/${dateFields.dobYear}`
                }

                if (!!ffk.formField && ffk.formField.fieldValue && ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirthJointLife) {
                    ffk.formField.fieldValue = `${dateFields.jlDobDay.padStart(2, "0")}/${dateFields.jlDobMonth.padStart(2, "0")}/${dateFields.jlDobYear}`
                }

                _formField = ffk.formField || {
                    id: 0,
                    fieldValue: ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ?
                        `${dateFields.dobDay.padStart(2, "0")}/${dateFields.dobMonth.padStart(2, "0")}/${dateFields.dobYear}` :
                        ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirthJointLife ?
                            `${dateFields.jlDobDay.padStart(2, "0")}/${dateFields.jlDobMonth.padStart(2, "0")}/${dateFields.jlDobYear}` : "",
                    fieldValueTest: "",
                    formFieldKeyId: ffk.id,
                    formTemplateSectionId: ffk.formTemplateSectionId,
                    formSectionId: params.formSectionId ?? 0
                }


                if (ffk.formFieldValueTypeId === FormFieldValueTypeEnum.Signature) {
                    _formField.fieldValue = (signatureRef.current as any).getTrimmedCanvas().toDataURL("image/png");
                }

                return _formField;
            });

            var _userData = {
                ...userData,
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.FirstName]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.FirstName),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.LastName]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.LastName),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.DateOfBirth]]:
                    getDobValue(
                        FormFieldKeySpecialTypeEnum.DateOfBirth,
                        dateFields?.dobDay,
                        dateFields?.dobMonth,
                        dateFields?.dobYear
                    ),

                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.EmailAddress]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.EmailAddress),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.PhoneNumber]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.PhoneNumber),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.SmokingStatus]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.SmokingStatus),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.Gender]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.Gender),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.NumberOfCovers]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.NumberOfCovers),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.FirstNameJointLife]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.FirstNameJointLife),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.LastNameJointLife]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.LastNameJointLife),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.DateOfBirthJointLife]]:
                    getDobValue(
                        FormFieldKeySpecialTypeEnum.DateOfBirthJointLife,
                        dateFields?.jlDobDay,
                        dateFields?.jlDobMonth,
                        dateFields?.jlDobYear
                    ),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.SmokingStatusJointLife]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.SmokingStatusJointLife),
                [FormFieldKeySpecialTypeEnum[FormFieldKeySpecialTypeEnum.GenderJointLife]]: getFormFieldKeyDataFromFormFieldKeySpecialTypeEnum(FormFieldKeySpecialTypeEnum.GenderJointLife),

            }

            // Save to local storage
            setUserData(_userData)
            localStorage.setItem('userData', JSON.stringify(_userData));

        }

        var form = {
            id: params.formSectionId ?? 0,
            formId: _formId ?? 0,
            formTemplateSectionId: params.formTemplateSectionId ?? 0,
            formSectionId: params.formSectionId ?? 0,
            formFields: _formFields
        };

        var formData = new FormData();
        formData.append(`id`, form.id.toString());
        formData.append(`formId`, form.formId.toString());
        formData.append(`formTemplateSectionId`, form.formTemplateSectionId.toString());
        formData.append(`formSectionId`, form.formSectionId.toString());

        form.formFields.forEach((formField, index) => {
            for (var formFieldName in formField) {
                var val = (formField as any)[formFieldName];
                formData.append(`formFields[${index}].${formFieldName}`, val || (val === null || val === "" || val === " " ? "" : 0));
            }
        });

        var documentUploadParentFields = document.getElementsByClassName("documentUpload");
        var documentsToClear: Array<string> = []

        if (documentUploadParentFields && documentUploadParentFields.length > 0) {
            var currentIndex = 0;
            Array.from(documentUploadParentFields).forEach((documentUploadParentField: any, index: number) => {

                var fileField = documentUploadParentField.getElementsByClassName("MuiInputBase-input")[0];
                documentsToClear.push(fileField.id);

                var id = fileField.id.split("_")[1];

                if (data && data.formFieldKeys && data.formFieldKeys.length && (fileField as any)?.files[0]) {
                    formData.append("file[" + currentIndex + "].file", (fileField as any)?.files[0]);
                    formData.append("file[" + currentIndex + "].formFieldKeyId", id);
                    currentIndex++
                }
            })
        }

        setIsSaving(true);


        formSectionService[params.actionId ? "updateOnFormActionSave" : "postFromData"](formData).then(
            async (data) => {
                documentsToClear.forEach(d => {
                    var doc = document.getElementById(d);
                    (doc as any).value = null;
                });
                params.onSave();

                //if (params.activeStep == 1) {
                //    notifyParentConversion();
                //    getQuotes();
                //}
                if (params.activeStep == 1) {
                    await notifyParentConversionWithAudit({
                        formId,
                        quoteId: null,
                        brandId: params.brand,
                        activeStep: params.activeStep ?? null,
                        eventName: "quote_form_submission",
                        pageUrl: window.location.href,
                        sentRef: conversionSentRef,
                        parentWindow: window.parent,
                        saveQuoteSubmittedTracking: true
                    });

                    getQuotes();
                }

            }, handleErrorResponseAxios)
            .finally(() => {
                setIsSaving(false);
            });
    }

    function valuetext(value: number) {
        return getMoneyFormatInteger(value);
    }

    function valuetextyears(value: number) {
        return value + "yrs";
    }

    function getSliderMarks(minValue?: number, maxValue?: number, shortName?: string) {
        minValue = minValue || 0;
        maxValue = maxValue || 9999;
        var marks = [];

        if (screenSizeCategory === ScreenSizeCategoryEnum.LG || screenSizeCategory === ScreenSizeCategoryEnum.XL) {

            var val = Math.ceil(maxValue / 4);
            for (var i = 0; i < 5; i++) {
                marks.push({
                    value: val * i,
                    label: shortName === "term" || shortName === "nra" ? valuetextyears(val * i) : valuetext(val * i),
                })
            }
            return marks;
        } else {
            marks.push({
                value: minValue,
                label: shortName === "term" || shortName === "nra" ? valuetextyears(minValue) : valuetext(minValue),
            }, {
                value: maxValue,
                label: shortName === "term" || shortName === "nra" ? valuetextyears(maxValue) : valuetext(maxValue),
            })
            return marks;
        }

    }

    function getField(field: IFormFieldKeyManage, index: number) {
        var validators = [];
        var validatorMessages = [];
        var isDisabled = params.isForReview;
        var variant = "outlined";

        if (field.isRequired) {
            validators.push("required");
            validatorMessages.push("This field is required");
        }

        switch (field.formFieldValueTypeId) {
            case (FormFieldValueTypeEnum.String):
                return <FormControl fullWidth>
                    <Box display="flex" flexDirection="column">
                        <FormLabel required error={!field.formField?.fieldValue && isSaving === true} id={field.label}>
                            {field.label}
                        </FormLabel>
                        {!!field.description &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Box>
                    <Grid container>
                        <Grid item xs={12}>
                            <TextValidatorHcs
                                value={field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                placeholder={field.placeholder}
                                required={field.isRequired}
                                fullWidth
                                disabled={isDisabled}
                                type={"text"}
                                variant={variant}
                                validators={[...validators]}
                                errorMessages={[...validatorMessages]}
                                onChange={(e: any) => { handleChange(e, index); }} />
                        </Grid>
                    </Grid>
                </FormControl>
                break;

            case (FormFieldValueTypeEnum.Date):
                return <FormControl fullWidth>
                    <FormLabel required={field.isRequired} error={!field.formField?.fieldValue && isSaving === true} id={field.label}>
                        {field.label}
                    </FormLabel>
                    <Box display="flex" mt={0} gap={1} alignItems="center">
                        {/* Day */}
                        <TextField type="number"
                            label="DD"
                            value={field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ? dateFields.dobDay : dateFields.jlDobDay}
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, "");
                                if (field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth) {
                                    setDateFields({ ...dateFields, dobDay: val });
                                    if (val.length === 2) dobMonthRef.current?.focus();
                                } else {
                                    setDateFields({ ...dateFields, jlDobDay: val });
                                    if (val.length === 2) jlDobMonthRef.current?.focus();
                                }
                            }}
                            placeholder="DD"
                            inputProps={{ maxLength: 2 }}
                            sx={{ width: "70px" }}
                        />
                        /
                        {/* Month */}
                        <TextField type="number"
                            inputRef={
                                field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth
                                    ? dobMonthRef
                                    : jlDobMonthRef
                            }
                            label="MM"
                            value={field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ? dateFields.dobMonth : dateFields.jlDobMonth}
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, "");
                                if (field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth) {
                                    setDateFields({ ...dateFields, dobMonth: val });
                                    if (val.length === 2) dobYearRef.current?.focus();
                                } else {
                                    setDateFields({ ...dateFields, jlDobMonth: val });
                                    if (val.length === 2) jlDobYearRef.current?.focus();
                                }
                            }}
                            placeholder="MM"
                            inputProps={{ maxLength: 2 }}
                            sx={{ width: "70px" }}
                        />
                        /
                        {/* Year */}
                        <TextField type="number"
                            inputRef={
                                field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth
                                    ? dobYearRef
                                    : jlDobYearRef
                            }
                            label="YYYY"
                            value={field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ? dateFields.dobYear : dateFields.jlDobYear}
                            onChange={(e) => {
                                const val = e.target.value.replace(/[^0-9]/g, "");
                                if (field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth) {
                                    setDateFields({ ...dateFields, dobYear: val });
                                } else {
                                    setDateFields({ ...dateFields, jlDobYear: val });
                                }
                            }}
                            placeholder="YYYY"
                            inputProps={{ maxLength: 4 }}
                            sx={{ width: "100px" }}
                        />
                    </Box>

                    {/* Hidden validator field for DOB validation */}
                    <TextValidatorHcs
                        sx={{ ".MuiInputBase-root": { display: "none" } }}
                        name={`${field.shortName}-dateGroup`}
                        value={`${field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ? dateFields.dobDay : dateFields.jlDobDay}/${field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ? dateFields.dobMonth : dateFields.jlDobMonth}/${field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.DateOfBirth ? dateFields.dobYear : dateFields.jlDobYear}`}
                        validators={["isValidDateGroup"]}
                        errorMessages={["Invalid date of birth"]}
                    />
                </FormControl>

                break;
            case (FormFieldValueTypeEnum.Email):
                return <FormControl fullWidth>
                    <Box display="flex" flexDirection="column">
                        <FormLabel required={field.isRequired} error={!field.formField?.fieldValue && isSaving === true} id={field.label}>
                            {field.label}
                        </FormLabel>
                        {!!field.description &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Box>
                    <Grid container>
                        <Grid item xs={12}>
                            <TextValidatorHcs
                                value={field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                required={field.isRequired}
                                fullWidth
                                type={"email"}
                                disabled={isDisabled}
                                variant={variant}
                                validators={[...validators, "isEmail"]}
                                errorMessages={[...validatorMessages, 'Email is not valid']}
                                onChange={(e: any) => { handleChange(e, index) }}

                            />
                        </Grid>
                    </Grid>
                </FormControl>
                break;
            case (FormFieldValueTypeEnum.PhoneNumber):
                return <FormControl fullWidth>
                    <Box display="flex" justifyContent="flex-start">
                        <FormLabel required error={!field.formField?.fieldValue && isSaving === true} id={field.label}>
                            {field.label}
                        </FormLabel>
                        {!!field.description &&
                            <Alert>{field.description}</Alert>
                        }
                    </Box>
                    <Grid container>
                        <Grid item xs={12}>
                            <TextValidatorHcs
                                key={index}
                                value={field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                required={field.isRequired}
                                variant={variant}
                                fullWidth
                                type={"phone"}
                                disabled={isDisabled}
                                validators={field.isRequired ? ['required', 'isValidPhoneNumber'] : ['isValidPhoneNumber']}
                                errorMessages={field.isRequired ? ['This field is required', 'Enter a valid phone number'] : ['Enter a valid phone number']}
                                onChange={(e: any) => { handleChange(e, index, false, true) }}

                            />
                        </Grid>

                    </Grid>
                </FormControl>
                break;
          
            case (FormFieldValueTypeEnum.Dropdown):
                return <FormControl fullWidth>
                    <Box display="flex" flexDirection="column">
                        <FormLabel error={!field.formField?.fieldValue && isSaving === true} id={field.label} required={field.isRequired}>{field.label}</FormLabel>
                        {
                            !!field.description &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Box>
                    <Grid container>
                        <Grid item xs={12}>
                            <Autocomplete
                                key={index}
                                options={field.formFieldKeyLookups!}
                                getOptionLabel={(option) => option.lookupValue}
                                onChange={(event, newValue) => {
                                    handleAutocompleteChange(event, index, newValue?.lookupValue);
                                }}
                                value={field.formFieldKeyLookups?.find((opt) => opt.lookupValue === field.formField?.fieldValue || null)}
                                renderInput={(params) => <TextField {...params} value={field.formField?.fieldValue || ""} />}
                            />
                        </Grid>
                        <TextValidatorHcs
                            sx={{ ".MuiInputBase-root": { display: "none" } }}
                            value={field.formField?.fieldValue}
                            name={field.shortName}
                            validators={["isAutocompleteValid"]}
                            errorMessages={[`This field is required`]}
                        />
                    </Grid>
                </FormControl>
                break
            case (FormFieldValueTypeEnum.Radio):
                return <FormControl>
                    <Grid>
                        <FormLabel id={field.label}>{field.label}</FormLabel>
                        {!!field.description &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Grid>
                    <RadioGroup
                        row={!field.formFieldKeyLookups?.some(l => l.description)} // row only if no descriptions
                        aria-labelledby={field.label}
                        onChange={(e: any) => handleChange(e, index)}
                        name={field.shortName}
                    >
                        {field.formFieldKeyLookups?.map((lookup) => (
                            <Grid
                                container
                                alignItems="center"
                                spacing={2}
                                key={"list_item_radio_" + field.id + "_" + lookup.id}
                            >
                                <Grid item>
                                    <FormControlLabel
                                        checked={
                                            !!field.formField?.fieldValue &&
                                            lookup.lookupValue.toLowerCase() ===
                                            field.formField?.fieldValue.toLowerCase()
                                        }
                                        value={lookup.lookupValue}
                                        control={
                                            <Radio
                                                disabled={isDisabled}
                                                key={"list_item_radio_sub_" + field.id + "_" + lookup.id}
                                            />
                                        }
                                        label={lookup.label}
                                    />
                                </Grid>
                                {lookup.description && (
                                    <Grid item>
                                        <span style={{ fontSize: "0.9rem", color: "#666" }}>
                                            {lookup.description}
                                        </span>
                                    </Grid>
                                )}
                            </Grid>
                        ))}
                    </RadioGroup>
                    {/*<RadioGroup*/}
                    {/*    row*/}
                    {/*    aria-labelledby={field.label}*/}
                    {/*    onChange={(e: any) => { handleChange(e, index) }}*/}
                    {/*    name={field.shortName}>*/}

                    {/*    {field.formFieldKeyLookups?.map(lookup =>*/}
                    {/*        <FormControlLabel key={"list_item_radio_" + field.id + "_" + lookup.id} required={field.isRequired} checked={(!!field.formField?.fieldValue && lookup.lookupValue.toLowerCase() == field.formField?.fieldValue.toLowerCase()) ? true : false} value={lookup.lookupValue} control={*/}
                    {/*            <Radio disabled={isDisabled} key={"list_item_radio_sub_" + field.id + "_" + lookup.id} />} label={lookup.label}*/}
                    {/*        />*/}
                    {/*    )}*/}

                    {/*</RadioGroup>*/}
                </FormControl>
                break;

            case (FormFieldValueTypeEnum.Text):
                return <Typography textAlign="left" variant={field.textFontSize ?? "h6"}>{field.label}</Typography>
                break;
            case (FormFieldValueTypeEnum.Textarea):
                return <>
                    <Box display="flex" flexDirection="column">
                        <FormLabel required={field.isRequired} error={!field.formField?.fieldValue && isSaving === true} id={field.label}>
                            {field.label}
                        </FormLabel>
                        {!!field.description &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Box>
                    <TextValidatorHcs
                        label={field.label}
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        placeholder={field.placeholder}
                        required={field.isRequired}
                        fullWidth
                        multiline
                        type={"text"}
                        rows={4}
                        hintText="test"
                        disabled={isDisabled}
                        variant={variant}
                        validators={field.isRequired ? ['required'] : []}
                        errorMessages={field.isRequired ? ['This field is required'] : []}
                        onChange={(e: any) => { handleChange(e, index) }}
                    />
                </>
                break;
            case (FormFieldValueTypeEnum.Number):
                return <FormControl fullWidth required error={!field.formField?.fieldValue && isSaving === true}>
                    <Box display="flex" flexDirection="column">
                        <FormLabel id={field.label} required={field.isRequired}>
                            {data?.formFieldKeys?.some((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.MortgageType &&
                                data.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.MortgageType)[0].formField?.fieldValue.includes("Switching")) ?
                                field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.PropertyValue ? "What is the estimated value of your property?" :
                                    field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount ? "What is the balance of your mortgage?" :
                                        field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Term ? "What is the remaining term of your mortgage?" :
                                            field.label : field.label}
                        </FormLabel>

                        {(!!field.description && !params.actionId) &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextValidatorHcs
                                value={field.maxValue && Number(field.formField?.fieldValue) > (data?.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount &&
                                    data?.formFieldKeys?.some((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income &&
                                        data.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) ?
                                    (Number(data?.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) * 4) : field.maxValue) ?
                                    field.formField?.fieldValue ?? "" : field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                required={field.isRequired}
                                fullWidth
                                type={"number"}
                                disabled={isDisabled}
                                variant={variant}
                                validators={field.isRequired ? ['required', 'isNumber', `minNumber:${field.minValue}`, `maxNumber:${field.maxValue}`,] :
                                    ['isNumber', `minNumber:${field.minValue}`, `maxNumber:${field.maxValue}`]}
                                errorMessages={field.isRequired ? ['This field is required', 'Only numbers are allowed.',
                                    `${field.label} must be minimum ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                        getMoneyFormatNoFractions(field.minValue!) : field.shortName === "nra" ? field.minValue + "yrs" : field.minValue}`,
                                    `${field.label} must be maximum ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                        getMoneyFormatNoFractions(field.maxValue!) : field.shortName === "nra" ? field.maxValue + "yrs" : field.maxValue}`] :
                                    ['Only numbers are allowed.', `${field.label} must be minimum ${getMoneyFormatNoFractions(field.minValue!)}`,
                                        `${field.label} must be maximum ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                            getMoneyFormatNoFractions(field.maxValue!) : field.shortName === "nra" ? field.maxValue + "yrs" : field.maxValue}`]}

                                onChange={(e: any) => { handleChange(e, index) }}
                                InputProps={{
                                    inputProps: {
                                        step: field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ? 10000 :
                                            field.shortName === "nra" ? 5 : 1
                                    },
                                    min: field.minValue,
                                    //max: field.maxValue
                                }}
                                placeholder={field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount ||
                                    field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmountJointLife ||
                                    field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.PropertyValue ? "Example: 200000" :
                                    field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Term ? "Example: 25" :
                                        field.label}
                            />


                            {data?.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount &&
                                data?.formFieldKeys?.some((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income &&
                                    data.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) &&
                                <Alert severity="info">You can borrow up to 4x your income.</Alert>
                            } </Grid>
                    </Grid>
                </FormControl>
                break;

            case (FormFieldValueTypeEnum.Signature):
                return !params.isForReview ? (
                    <div>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}>
                            {field.label}
                            {field.isRequired && <span style={{ color: "red" }}> *</span>}
                        </label>
                        <div style={{ border: "1px solid silver" }}>
                            <SignatureCanvas
                                penColor="black"
                                ref={signatureRef}
                                canvasProps={{ width: 800, height: 200, className: 'sigCanvas' }}
                            />
                        </div>
                    </div>
                ) : (
                    <div>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: 500 }}>
                            {field.label}
                            {field.isRequired && <span style={{ color: "red" }}> *</span>}
                        </label>
                        <img src={field.formField?.fieldValue} style={{ maxWidth: "100%" }} width="800px" height="200px" />
                    </div>
                );

                break;

            case (FormFieldValueTypeEnum.List):
                return <>

                    {field.showLabel && <Typography>
                        {field.label}
                    </Typography>
                    }

                    <List sx={{ listStyle: "decimal", pl: 4 }}>

                        {field.formFieldKeyLookups?.map(lookup =>
                            <ListItem key={"list_item_list_" + field.id} sx={{ display: "list-item" }}>
                                <ListItemText primary={lookup.label || lookup.lookupValue} />
                            </ListItem>
                        )}
                    </List></>
                break;
            case (FormFieldValueTypeEnum.Title):
                return <Typography color="primary" fontWeight="800" variant="h5" textAlign="center">{field.label}</Typography>
                break;
            case (FormFieldValueTypeEnum.Image):
                return <img style={{
                    width: "100%",
                    marginTop: "10px",
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    marginBottom: "10px"
                }} src={"data:image/jpeg;charset=utf-8;base64," + field.label} alt={""}></img>
                break;
            case (FormFieldValueTypeEnum.Checkbox):
                return <FormControlLabel
                    control={<Checkbox sx={{ padding: 1.5 }} color="primary"
                        checked={field.formField?.fieldValue?.toLowerCase() === "yes"}
                        onChange={(e) => handleChange(e, index, true)}
                    />}
                    //label={field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.TermsAndConditions ?
                    //    <>
                    //        I have read and agree to LowQuotes{" "}
                    //        <a
                    //            href="https://lowquotes.ie/LQ-Terms-of-business.pdf"
                    //            target="_blank"
                    //            rel="noopener noreferrer"
                    //            style={{ textDecoration: "underline" }}
                    //        >
                    //            Terms of business and Privacy Policy
                    //        </a>
                    //    </> : field.label
                    //}
                    label={field.label}
                />
                break;

            case (FormFieldValueTypeEnum.RadioButton):
                return <FormControl fullWidth required error={!field.formField?.fieldValue && isSaving === true}>
                    <Box display="flex" flexDirection="column">
                        <FormLabel error={!field.formField?.fieldValue && isSaving === true} id={field.label}>{field.label}</FormLabel>
                        {!!field.description &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Box>
                    <Grid container spacing={1} padding={0}>
                        {field.formFieldKeyLookups?.map(lookup =>
                            <Grid item xs={(field.formFieldKeyLookups?.length || 0) % 8 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 7 == 0 ? 3 : (field.formFieldKeyLookups?.length || 0) % 6 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 5 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 4 == 0 ? 6 : (field.formFieldKeyLookups?.length || 0) % 3 == 0 ? 4 : (field.formFieldKeyLookups?.length || 0) % 2 == 0 ? 6 : 12}>
                                <ToggleButton disabled={isDisabled} fullWidth color="primary" onChange={(e: any) => { handleChange(e, index) }} selected={!!field.formField?.fieldValue && field.formField?.fieldValue.toLowerCase() === lookup.lookupValue.toLowerCase()} value={lookup.lookupValue} aria-label="left aligned">
                                    {lookup.lookupValue}
                                </ToggleButton>

                            </Grid>
                        )}
                        {/* Validation error */}
                        <TextValidatorHcs
                            value={field.formField?.fieldValue || ""}
                            name="toggleSelection"
                            validators={["isRequiredToggle"]}
                            errorMessages={[`This field is required`]}
                            sx={{ ".MuiInputBase-root": { display: "none" } }}
                        />

                    </Grid>

                </FormControl>
                break;

            case (FormFieldValueTypeEnum.Slider):
                return <FormControl fullWidth required error={!field.formField?.fieldValue && isSaving === true}>
                    <Box display="flex" flexDirection="column">
                        <FormLabel id={field.label} required={field.isRequired}>{field.label}: {field.shortName === "term" || field.shortName === "nra" ?
                            valuetextyears(Number(field.formField?.fieldValue || 0)).toString() :
                            valuetext(Number(field.formField?.fieldValue || 0)).toString()
                        }
                        </FormLabel>

                        {(!!field.description && !params.actionId) &&
                            <Alert severity="info">{field.description}</Alert>
                        }
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={5} md={4}>
                            <TextValidatorHcs
                                value={field.maxValue && Number(field.formField?.fieldValue) > (data?.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount &&
                                    data?.formFieldKeys?.some((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income &&
                                        data.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) ?
                                    (Number(data?.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) * 4) : field.maxValue) ?
                                    0 : field.formField?.fieldValue ?? ""}
                                name={field.shortName}
                                required={field.isRequired}
                                fullWidth
                                type={"number"}
                                disabled={isDisabled}
                                variant={variant}
                                validators={field.isRequired ? ['required', 'isNumber', `minNumber:${field.minValue}`, `maxNumber:${field.maxValue}`,] :
                                    ['isNumber', `minNumber:${field.minValue}`, `maxNumber:${field.maxValue}`]}
                                errorMessages={field.isRequired ? ['This field is required', 'Only numbers are allowed.',
                                    `Value must be at least ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                        getMoneyFormatNoFractions(field.minValue!) : field.shortName === "nra" ? field.minValue + "yrs" : field.minValue}`,
                                    `Value must be at most ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                        getMoneyFormatNoFractions(field.maxValue!) : field.shortName === "nra" ? field.maxValue + "yrs" : field.maxValue}`] :
                                    ['Only numbers are allowed.', `Value must be at least ${getMoneyFormatNoFractions(field.minValue!)}`,
                                        `Value must be at most ${field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ?
                                            getMoneyFormatNoFractions(field.maxValue!) : field.shortName === "nra" ? field.maxValue + "yrs" : field.maxValue}`]}
                                onInput={handleInput}
                                onChange={(e: any) => { handleChange(e, index) }}
                                InputProps={{
                                    inputProps: {
                                        step: field.shortName === "coveramount" || field.shortName === "coveramountjointlife" ? 10000 :
                                            field.shortName === "nra" ? 5 : 1
                                    },
                                    min: field.minValue,
                                    max: field.maxValue
                                }}
                                placeholder={"Type specific value"}
                            />
                        </Grid>
                        <Grid item xs={7} md={8}>

                            <Slider
                                aria-label="Custom marks"
                                defaultValue={field.minValue || 0}
                                value={Number(field.formField?.fieldValue || "")}
                                onChange={(e: any) => { handleChange(e, index) }}
                                getAriaValueText={field.shortName === "term" || field.shortName === "nra" ? valuetextyears : valuetext}
                                step={field.shortName === "term" || field.shortName === "nra" ? 5 : 10000}
                                min={field.minValue}
                                max={data?.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount &&
                                    data?.formFieldKeys?.some((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income &&
                                        data.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) ?
                                    (Number(data?.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) * 4) : field.maxValue}
                                valueLabelDisplay="off"
                                name={field.shortName}
                                marks={data?.formTemplateTypeId === FormTemplateTypeEnum.Mortgages && field.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.CoverAmount &&
                                    data?.formFieldKeys?.some((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income &&
                                        Number(data.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) > 0) ?
                                    getSliderMarks(field.minValue, (Number(data?.formFieldKeys!.filter((ffk: any) => ffk.formFieldKeySpecialTypeId === FormFieldKeySpecialTypeEnum.Income)[0].formField?.fieldValue) * 4), field.shortName)
                                    : getSliderMarks(field.minValue, field.maxValue, field.shortName)}
                                disabled={isDisabled}
                            />
                        </Grid>

                    </Grid>
                </FormControl>
                break;
            case (FormFieldValueTypeEnum.WarningAlert):
                return <Alert severity="warning">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.InfoAlert):
                return <Alert severity="info">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.SuccessAlert):
                return <Alert severity="success">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.DangerAlert):
                return <Alert severity="error">{field.label}</Alert>
                break;
            case (FormFieldValueTypeEnum.DataTypeAutoComplete):
                return <>
                    <DataTypeAutoComplete
                        dataType={field.dataType}
                        label={field.label}
                        valueField="name"
                        value={field.formField?.fieldValue ?? ""}
                        name={field.shortName}
                        required={field.isRequired}
                        disabled={isDisabled}
                        onChange={(e: any) => {
                            handleChangeDataType(e, index)
                        }}
                    />
                </>
                break;
            case (FormFieldValueTypeEnum.Link):
                return <>
                    <Link
                        href={field.label}
                        sx={(theme) => ({
                            color: theme.palette.secondary.main,
                        })}
                        underline="always"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {field.description}
                    </Link>
                </>
                break;
            default: return ";";
        }
    }

    function isFormValid(e: any) {
        console.log("is form valud", e);
    }

    function checkIfDependancyMet(formFieldKey: IFormFieldKeyManage) {
        if ((!formFieldKey.formFieldKeyFormFieldKeyLookups || !formFieldKey.formFieldKeyFormFieldKeyLookups.length) && (!formFieldKey.formFieldKeyConditionDependencies || !formFieldKey.formFieldKeyConditionDependencies.length))
            return true;

        var _return: boolean | null = null;
        if (formFieldKey.formFieldKeyFormFieldKeyLookups && formFieldKey.formFieldKeyFormFieldKeyLookups.length) {


            formFieldKey.formFieldKeyFormFieldKeyLookups.forEach(ffkfl => {
                if (data && data.formFieldKeys && data.formFieldKeys) {
                    data.formFieldKeys.filter(ffk => ffk.formFieldKeyLookups).forEach(ffk => {
                        if (ffk.formFieldKeyLookups?.some(ffkl => ffkl.id == ffkfl.formFieldKeyLookupId)) {
                            if (!!ffk && !!ffk.formField && ffk.formField.fieldValue == ffkfl.value) _return = true;
                        }
                    });

                    var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkfl.formFieldKeyLookupId);
                    if (globalFormField && globalFormField.fieldValue && globalFormField.fieldValue.toString().toLowerCase() === ffkfl.value.toString().toLowerCase()) {
                        _return = true;
                    }
                }
            });
        }

        if (formFieldKey.formFieldKeyConditionDependencies && formFieldKey.formFieldKeyConditionDependencies.length) {
            formFieldKey.formFieldKeyConditionDependencies.forEach((ffkcd: any) => {
                var globalFormField = data?.globalFormFields?.find(m => m.formFieldKeyId == ffkcd.conditionFormFieldKeyId);

                if (!globalFormField) {
                    _return = false;
                    return;
                }
                if (_return === null ||
                    (_return === true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.All) ||
                    (_return !== true && formFieldKey.formFieldKeyConditionTypeId === FormFieldKeyConditionTypeEnum.Any)) {

                    if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsTo) {
                        _return = globalFormField.fieldValue.toString().toLowerCase() === ffkcd.value.toString().toLowerCase();
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrGreater) {
                        _return = Number(globalFormField.fieldValue) >= Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.EqualsOrLess) {
                        _return = Number(globalFormField.fieldValue) <= Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Less) {
                        _return = Number(globalFormField.fieldValue) < Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Greater) {
                        _return = Number(globalFormField.fieldValue) > Number(ffkcd.value);
                    }
                    else if (ffkcd.formFieldKeyConditionId === FormFieldKeyConditionEnum.Contains) {
                        _return = globalFormField.fieldValue && ffkcd.value && globalFormField.fieldValue.trim().toLowerCase().includes(ffkcd.value.trim().toLowerCase());
                    }
                }


            });
        }

        if (_return === null)
            _return = false;
        return _return;
    }

    function getSections() {
        var subSections: any[] = [];
        if (data && data.formFieldKeys && data.formFieldKeys) {
            subSections = data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId === FormFieldValueTypeEnum.SubSection);
            if (data.formFieldKeys.some(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && !ffk.subSectionId))
                subSections.push({});
        }
        return subSections || [{}];
    }

    function getSectionKeys(subSectionId?: any) {
        var keys: any[] = [];
        if (data && data.formFieldKeys && data.formFieldKeys) {
            if (data.formtemplateTypeId === FormTemplateTypeEnum.MortgageProtection) {
                data.formFieldKeys = data.formFieldKeys.filter(ffk => ffk.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.CoverAmountJointLife);
            }
            return data.formFieldKeys.filter(ffk => ffk.formFieldValueTypeId !== FormFieldValueTypeEnum.SubSection && ffk.subSectionId == subSectionId)
        }

        return [];
    }

    return (
        <Grid container spacing={1} id="form-manage" key={"form_template_section_" + params.formTemplateSectionId}>
            <Grid item xs={12}>

                {(isLoading || isSaving || params.isSectionSaving) &&
                    <Paper sx={!!params.actionId ? { backgroundColor: 'transparent' } : { backgroundColor: 'auto' }} >
                        <Typography variant="body1" className="paper-theme-body" gutterBottom>
                            <LinearProgress color="secondary" />
                        </Typography>
                    </Paper>
                }

                <ValidatorForm isFormValid={isFormValid}
                    onSubmit={submit} noValidate

                    onError={(errors: any) => {
                        // Scroll to the first error
                        if (errors.length > 0) {
                            const messages = errors.map((err: any) => err.props.errorMessages[0]);
                            const uniqueMessages = messages.filter((value: any, index: any, self: any) => self.indexOf(value) === index);
                            setErrorMessages(uniqueMessages);

                            const firstError = errors[0];
                            if (firstError && firstError.props && firstError.props.name) {
                                const errorElements = document.getElementsByName(firstError.props.name);
                                if (errorElements.length > 0) {
                                    const errorElement = errorElements[0];
                                    errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
                                    errorElement.focus();
                                }
                            }
                        }
                    }}
                >

                    {getSections().filter(s => checkIfDependancyMet(s)).map((s, index) =>

                        <Paper key={"section_" + s.id + "_" + index}
                            sx={{
                                marginBottom: !!params.actionId ? "8px" : "8px",
                                marginTop: "32px !important",
                                paddingTop: "11px",
                                backgroundColor: { xs: !!params.actionId ? 'transparent' : 'auto', md: !!params.actionId ? "#f6f6f6" : "auto" },
                                border: {
                                    lg: !!params.actionId ? `1px solid ${PRIMARY_COLOR}` : 'auto'
                                }
                            }}>

                            <Grid container spacing={2}
                                alignItems="center"
                                sx={{
                                    maxHeight: {
                                        xs: 'auto',
                                        sm: 'auto',
                                        md: 'auto',
                                        lg: params.actionId ? '140px' : 'auto',
                                        xl: params.actionId ? '140px' : 'auto'
                                    },
                                }}>
                                {getSectionKeys(s.id).filter(s => s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.IndexationAdmin
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.SeriousIllnessCoverTypeAdmin
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.SeriousIllnessCoverAmountAdmin
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.SeriousIllnessCoverAmountJointLifeAdmin
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.ConvertibleOptionAdmin
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.QuoteResultsActionAdmin
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.IncomeProtectionCoverAmountAdmin
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.IncomeProtectionCoverAmountAvailable
                                    && s.formFieldKeySpecialTypeId !== FormFieldKeySpecialTypeEnum.NotesAdmin

                                    && checkIfDependancyMet(s)).map((ffk, index) => <>
                                        <Grid key={"subsection" + s.id + "_" + index + "_" + ffk.id} item xs={ffk.xsWidth || 12} md={params.actionId ? getSectionKeys(s.id).length == 3 ? 4 : 6 : ffk.mdWidth || 12}>
                                            {getField(ffk, ffk.id)}
                                        </Grid>

                                    </>)}
                            </Grid>

                        </Paper>
                    )}

                    {!params.isForReview &&
                        <Paper>
                            <Grid container mt={3}>
                                <PrimaryButton variant="primary" styles={{ minHeight: "60px", maxHeight: "200px", height: "60px !important" }} isSaving={isLoading || isSaving || params.isSectionSaving} type="submit" width={params.actionId ? "200px" : "100%"}>
                                    <Typography variant="h5" fontWeight="bold">{params.activeStep === 1 ? "Get Quote" : "Continue"}</Typography>
                                </PrimaryButton>
                            </Grid>
                        </Paper>
                    }

                </ValidatorForm>

            </Grid>
            <CenteredAlert
                open={alertOpen}
                alertText="You must agree to the terms."
                severity="error"
                borderColor="red"
                onClose={() => setAlertOpen(false)}
            />
        </Grid>
    );
}